import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  BlendProgress,
  BlendResult,
  BlendStatus,
  BlendStatusResponse,
} from '@/types/report-blender';
import { toError } from '@/utils/errors';
import { usePoll } from './usePoll';

const DEFAULT_POLL_INTERVAL = 5000; // 5 seconds
const MAX_POLL_DURATION = 600000; // 10 minutes

export type BlendFetchState = 'idle' | 'fetching' | 'polling' | 'success' | 'error' | 'not_found';

interface UseBlendedReportOptions {
  /**
   * Polling interval in milliseconds
   * @default 5000
   */
  pollInterval?: number;
  /**
   * Callback when blend completes successfully
   */
  onSuccess?: (result: BlendResult) => void;
  /**
   * Callback when blend fails
   */
  onError?: (error: Error) => void;
  /**
   * Callback on each progress update
   */
  onProgress?: (progress?: BlendProgress) => void;
  /**
   * Whether to automatically fetch on mount
   * @default true
   */
  autoFetch?: boolean;
}

interface UseBlendedReportReturn {
  /**
   * Fetch the blended report for a subject
   */
  fetchBlend: (subjectId: string) => Promise<void>;
  /**
   * Cancel polling
   */
  cancel: () => void;
  /**
   * Current state of the fetch operation
   */
  state: BlendFetchState;
  /**
   * Current status from the API
   */
  status: BlendStatus | null;
  /**
   * Progress information (if available)
   */
  progress: BlendProgress | null;
  /**
   * Blend result (if completed)
   */
  result: BlendResult | null;
  /**
   * Error message (if failed)
   */
  error: string | null;
  /**
   * Whether a fetch/poll is in progress
   */
  isLoading: boolean;
  /**
   * Whether currently polling for status
   */
  isPolling: boolean;
  /**
   * Blended report ID
   */
  blendedReportId: string | null;
  /**
   * Thread ID of the blend
   */
  threadId: string | null;
  /**
   * Message from the API
   */
  message: string | null;
  /**
   * Partial results if blend failed but had some data
   */
  partialResults?: {
    structured_assignments: any[];
    partial_blended_report: string;
  };
}

// Extract error message from response
const getErrorMessage = (response: BlendStatusResponse): string => {
  return 'error' in response ? response.error : 'Blend operation failed with unknown error';
};

/**
 * Custom hook for fetching automatically-generated blended reports
 *
 * This hook fetches blended reports that were automatically created when
 * escalations completed. It handles polling for in-progress blends and
 * displays results when ready.
 *
 * @example
 * ```tsx
 * const { fetchBlend, state, progress, result, error } = useBlendedReport({
 *   onSuccess: (result) => console.log('Blend fetched!', result),
 *   onError: (error) => console.error('Failed to fetch:', error),
 * });
 *
 * // Fetch blend for a subject
 * useEffect(() => {
 *   fetchBlend(subjectId);
 * }, [subjectId]);
 *
 * // Display result
 * {state === 'success' && result && (
 *   <MarkdownParser content={result.blended_report} />
 * )}
 * ```
 */
export function useBlendedReport(options: UseBlendedReportOptions = {}): UseBlendedReportReturn {
  const { pollInterval = DEFAULT_POLL_INTERVAL, onSuccess, onError, onProgress } = options;

  const [state, setState] = useState<BlendFetchState>('idle');
  const [status, setStatus] = useState<BlendStatus | null>(null);
  const [progress, setProgress] = useState<BlendProgress | null>(null);
  const [result, setResult] = useState<BlendResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [blendedReportId, setBlendedReportId] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [partialResults, setPartialResults] = useState<any>(null);
  /** The blend whose status is being polled; null = not polling. */
  const [pollTarget, setPollTarget] = useState<string | null>(null);

  const isMountedRef = useRef(true);

  // Store callbacks in refs so the handlers stay stable
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onProgressRef = useRef(onProgress);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    onProgressRef.current = onProgress;
  }, [onSuccess, onError, onProgress]);

  // Handle completed blend
  const handleCompleted = useCallback((statusResponse: BlendStatusResponse) => {
    if ('result' in statusResponse) {
      setResult(statusResponse.result);
      setState('success');
      onSuccessRef.current?.(statusResponse.result);
    }
    setPollTarget(null);
  }, []);

  // Handle failed blend
  const handleFailed = useCallback((statusResponse: BlendStatusResponse) => {
    const errorMsg = getErrorMessage(statusResponse);
    setError(errorMsg);
    setState('error');

    if ('partial_results' in statusResponse && statusResponse.partial_results) {
      setPartialResults(statusResponse.partial_results);
    }

    const err = new Error(errorMsg);
    onErrorRef.current?.(err);
    Sentry.captureException(err);
    setPollTarget(null);
  }, []);

  // Handle errors during operations
  const handleError = useCallback(
    (err: unknown, defaultMessage = 'Failed to check blend status') => {
      if (!isMountedRef.current) {
        return;
      }
      const error = toError(err, defaultMessage);
      setError(error.message);
      setState('error');
      onErrorRef.current?.(error);
      Sentry.captureException(error);
      setPollTarget(null);
    },
    []
  );

  usePoll<BlendStatusResponse>({
    enabled: pollTarget !== null,
    interval: pollInterval,
    maxDurationMs: MAX_POLL_DURATION,
    refetchDeps: [pollTarget],
    queryFn: ({ signal }) => {
      if (!pollTarget) {
        throw new Error('no blend to poll');
      }
      return vnextApiClient.getBlendStatusById(pollTarget, false, { signal });
    },
    until: (statusResponse) =>
      statusResponse.status === 'completed' || statusResponse.status === 'failed',
    onData: (statusResponse) => {
      setStatus(statusResponse.status);
      setMessage(statusResponse.message);

      if (statusResponse.status === 'completed') {
        handleCompleted(statusResponse);
      } else if (statusResponse.status === 'failed') {
        handleFailed(statusResponse);
      } else if ('progress' in statusResponse && statusResponse.progress) {
        // pending / running (or unknown): keep polling, surface progress
        setProgress(statusResponse.progress);
        onProgressRef.current?.(statusResponse.progress);
      }
    },
    // A failed tick is not a failed blend: back off and try again.
    onError: (err) => Sentry.captureException(toError(err)),
    onStop: (reason) => {
      if (reason === 'maxDuration') {
        handleError(new Error('Blend fetch operation timed out after 10 minutes'));
      }
    },
  });

  // Cancel function
  const cancel = useCallback(() => {
    setPollTarget(null);
    setState('idle');
    setStatus(null);
    setProgress(null);
  }, []);

  // Fetch blend function - stable reference
  const fetchBlend = useCallback(
    async (subjectId: string) => {
      try {
        // Reset state
        setState('fetching');
        setStatus(null);
        setProgress(null);
        setResult(null);
        setError(null);
        setMessage(null);
        setPartialResults(null);
        setBlendedReportId(null);
        setThreadId(null);
        setPollTarget(null);

        // Fetch the latest blend for the subject
        const response: BlendStatusResponse =
          await vnextApiClient.getLatestBlendForSubject(subjectId);

        if (!isMountedRef.current) {
          return;
        }

        // Extract IDs from response
        if ('blended_report_id' in response && response.blended_report_id) {
          setBlendedReportId(response.blended_report_id);
        }
        if ('thread_id' in response && response.thread_id) {
          setThreadId(response.thread_id);
        }

        setStatus(response.status);
        setMessage(response.message);

        // Handle different states
        if (response.status === 'completed') {
          handleCompleted(response);
        } else if (response.status === 'pending' || response.status === 'running') {
          // Still processing - start polling
          if ('progress' in response && response.progress) {
            setProgress(response.progress);
          }
          if (response.blended_report_id) {
            setState('polling');
            setPollTarget(response.blended_report_id);
          }
        } else if (response.status === 'failed') {
          handleFailed(response);
        }
      } catch (err) {
        if (!isMountedRef.current) {
          return;
        }

        // Check for 404 - no blend exists (subject was never escalated)
        if (
          err instanceof Error &&
          (err.message.includes('404') || err.message.includes('Blended report not found'))
        ) {
          setState('not_found');
          setMessage('No blended report found for this subject');
          return;
        }

        handleError(err, 'Failed to fetch blend');
      }
    },
    [handleCompleted, handleError, handleFailed]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    fetchBlend,
    cancel,
    state,
    status,
    progress,
    result,
    error,
    isLoading: state === 'fetching' || state === 'polling',
    isPolling: state === 'polling',
    blendedReportId,
    threadId,
    message,
    partialResults,
  };
}
